import axios from "axios";

import { baseClient } from "@/api";
import appConfig from "@/config/app-config";
import { getBaseURL, getCachedAccountToken } from "@/contexts/auth/utils";
import { captureException } from "@/monitoring";
import { getTruthSessionIdRequestHeaders } from "@/utils/truth-session-id";
import { getVisitorTokenRequestHeaders } from "@/utils/visitor-token";

import type { AnalyticsEvent } from "@/schemas";

const ENDPOINT = "/api/v1/flow";

/** How the queue should treat a batch after a send attempt. */
type SendResult = "ok" | "drop" | "retry";

/**
 * POST a batch of analytics events (impressions + session start/end). `baseClient` handles
 * identity: Authorization when logged in, X-Visitor-Token when anonymous (attached + refreshed
 * via its interceptor), and X-Truth-Session-Id when a session exists. Response:
 *  - 2xx            -> "ok"    : accepted, remove from queue
 *  - 400 / 413      -> "drop"  : malformed / too large, will never succeed; report + drop
 *  - 401            -> "drop"  : rejected identity, nothing to retry with
 *  - 5xx / no reply -> "retry" : transient, keep queued and back off
 */
const sendImpressionBatch = async (
  events: AnalyticsEvent[],
): Promise<SendResult> => {
  if (appConfig.NODE_ENV === "test") return "ok";
  const api = baseClient(getCachedAccountToken());
  try {
    await api.post(ENDPOINT, { events });
    return "ok";
  } catch (error) {
    const status = axios.isAxiosError(error)
      ? error.response?.status
      : undefined;

    // Only malformed/too-large batches are genuine bugs worth reporting. A 401 (unresolved
    // identity) and transient no-response/5xx failures are expected — don't page on them.
    if (status === 400 || status === 413) {
      captureException(error);
      return "drop";
    }
    if (status === 401) return "drop";
    return "retry";
  }
};

/**
 * Fire-and-forget send for page unload, using `fetch` with `keepalive` (which, unlike
 * navigator.sendBeacon, can carry an auth header). Mirrors baseClient's identity choice.
 */
const sendImpressionsOnUnload = (events: AnalyticsEvent[]): void => {
  if (appConfig.NODE_ENV === "test") return;

  const token = getCachedAccountToken();
  try {
    void fetch(`${getBaseURL()}${ENDPOINT}`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        ...(token
          ? { Authorization: `Bearer ${token}` }
          : getVisitorTokenRequestHeaders()),
        ...getTruthSessionIdRequestHeaders(),
      },
      body: JSON.stringify({ events }),
    }).catch(() => {});
  } catch {
    /* the page is going away — nothing to do */
  }
};

export { sendImpressionBatch, sendImpressionsOnUnload, type SendResult };
